/**
 * Filters Autocomplete Repository
 * Handles autocomplete API calls for cascading filters
 */

import { string } from 'valibot'
import { apiClient } from '../../client'

export interface AutocompleteItem {
    /** Numeric entity id, or string for types like ref_id_1 */
    id: number | string
    en_name: string
    /** Present for user autocomplete; optional for roles and other entity types. */
    username?: string
    store_code?: string;
    /** SKU retail / unit price from autocomplete (maps from DB `unit_price`). */
    regular_price?: string | number | null
}

/** Label for filter options — ref_id_1 shows `1000234 - Store Name` when codes match. */
export function formatAutocompleteLabel(
    type: string,
    option: Pick<AutocompleteItem, 'id' | 'en_name' | 'username' | 'store_code'>,
): string {
    if (type === 'ref_id_1') {
        const ref = String(option.store_code || option.id || '').trim()
        const name = (option.en_name || '').trim()
        if (ref && name) return `${ref} - ${name}`
        return ref || name || ''
    }

    const extra = option.username || option.store_code
    return extra ? `${option.en_name} (${extra})` : option.en_name
}

export interface FiltersAutocompleteParams {
    type:
    'category'
    | 'subcategory'
    | 'brand'
    | 'department'
    | 'bu'
    | 'client'
    | 'region'
    | 'city'
    | 'store'
    | 'sku'
    | 'user'
    | 'photo_type'
    | 'visit_type'
    | 'client_promo_type'
    | 'client_promo_description'
    | 'promo_status'
    | 'bda_activity'
    | 'bda_type'
    | 'role'
    | 'chain'
    | 'channel'
    | 'store_format'
    | 'store_class'
    | 'rtv_type'
    | 'product_challenge_reason'
    | 'shift'
    | 'ref_id_1'
    | 'msl_typology'
    | 'pog_typology'
    | 'dashboard'
    | 'principal'
    | 'display_type'
    | 'display_size'
    | 'display_location'
    | 'sos_unit'
    | 'resident_region'
    | 'resident_city'
    | 'client_lta_subcategory'
    | 'client_lta_category'
    | 'ai_model'
    | 'survey'
    | 'photo_display_location'
    | 'bdas_status'




    search?: string
    filters?: {
        client_ids?: string
        bu_ids?: string
        department_ids?: string
        category_ids?: string
        subcategory_ids?: string
        brand_ids?: string
        region_ids?: string
        city_ids?: string
        store_ids?: string
        sku_ids?: string
        photo_type_ids?: string
        chain_ids?: string
        channel_ids?: string
        store_format_ids?: string
        store_class_ids?: string
        rtv_type_ids?: string
        challenge_type_ids?: string
        shift_ids?: string
        ref_ids?: string
        msl_typology?: string
        pog_typology?: string
        dashboard_ids?: string
        principal_ids?: string
        resident_city_ids?: string
        resident_region_ids?: string
        client_lta_subcategory_ids?: string;
        client_lta_category_ids?: string;
        ai_model_ids?: string;
        survey_ids?: string;
        user_type_ids?: string;
        /** 0 = Web, 1 = Mobile */
        access_to?: string;
        access_to_ids?: string;
        photo_display_location_ids?: string;
        bdas_status_ids?: string
    }
}

class FiltersAutocompleteRepository {
    /**
     * Get autocomplete results for filters
     * @param params - Autocomplete parameters including type, search, and filters
     * @returns Array of autocomplete items with id and en_name
     */
    async getAutocomplete(params: FiltersAutocompleteParams): Promise<AutocompleteItem[]> {
        // Send as POST request with JSON body
        const body = {
            type: params.type,
            ...(params.search && { search: params.search }),
            ...(params.filters && { filters: params.filters }),
        }

        return apiClient.post<AutocompleteItem[]>('/filters/autocomplete', body, true, { silent: true })
    }
}

export const filtersAutocompleteRepository = new FiltersAutocompleteRepository()
